import {fetchPoll} from '@github-ui/fetch-utils'
import {remoteForm, type Kicker} from '@github-ui/remote-form'
import {showGlobalError} from '../behaviors/ajax-error'

type MemoryExportResponse = {
  jobStatusUrl?: string
  downloadExportUrl?: string
}

const POLLING_INTERVAL_MS = 2_000
const MAX_POLLING_DURATION_MS = 15 * 60 * 1000

class MemoryExportPollingTimeoutError extends Error {
  constructor() {
    super('copilot_memory_admin_export_polling_timeout')
    this.name = 'MemoryExportPollingTimeoutError'
  }
}

function closeContainingDialog(form: HTMLFormElement) {
  const dialog = form.closest('modal-dialog') as (HTMLElement & {close?: (closedNotCancelled?: boolean) => void}) | null
  if (dialog?.close) {
    dialog.close(true)
    return
  }

  dialog?.querySelector<HTMLElement>('[data-close-dialog-id]')?.click()
}

function setLoadingState(submitButton: HTMLElement, generatingIndicator: HTMLElement, isLoading: boolean) {
  submitButton.hidden = isLoading
  generatingIndicator.hidden = !isLoading
}

async function pollMemoryExport(url: string) {
  const abortController = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      abortController.abort()
      reject(new MemoryExportPollingTimeoutError())
    }, MAX_POLLING_DURATION_MS)
  })

  try {
    await Promise.race([
      fetchPoll(
        url,
        {
          headers: {Accept: 'application/json'},
          signal: abortController.signal,
        },
        POLLING_INTERVAL_MS,
      ),
      timeout,
    ])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

async function generateMemoryExport(form: HTMLFormElement, wants: Kicker) {
  const generatingIndicator = form.parentElement?.querySelector<HTMLElement>('.js-copilot-memory-export-generating')
  const submitButton = form.querySelector<HTMLElement>('.js-copilot-memory-export-btn')
  if (!generatingIndicator || !submitButton) return

  setLoadingState(submitButton, generatingIndicator, true)

  let response
  try {
    response = await wants.json()
  } catch {
    setLoadingState(submitButton, generatingIndicator, false)
    showGlobalError()
    return
  }

  const data = response.json as MemoryExportResponse
  if (!data.jobStatusUrl || !data.downloadExportUrl) {
    setLoadingState(submitButton, generatingIndicator, false)
    showGlobalError()
    return
  }

  try {
    await pollMemoryExport(data.jobStatusUrl)
  } catch {
    if (!form.isConnected) return

    setLoadingState(submitButton, generatingIndicator, false)
    showGlobalError()
    return
  }

  if (!form.isConnected) return

  setLoadingState(submitButton, generatingIndicator, false)
  closeContainingDialog(form)
  await new Promise(resolve => setTimeout(resolve, 0))
  window.location.href = data.downloadExportUrl
}

remoteForm('.js-copilot-memory-export-form', async function (form, wants) {
  await generateMemoryExport(form, wants)
})
